import type { TemplateDefinition } from '../models';

/**
 * The kinds whose list, row menu, filter and view are drawn exactly as the design16 prototype: the PR and Supplier
 * Onboarding templates (2026-10-19), and the BID templates (2026-10-27, "PR and Supplier master change 21-10-26" --
 * the same Templates screen in that prototype). Their rules live in `styles/design16.css` under `templates-d16`,
 * and their icons are the prototype's Tabler glyphs (`styles/d16Icons.css`); the Default kind keeps PrimeIcons.
 */
export function isDesign16(definition: Pick<TemplateDefinition, 'kind'>): boolean {
  return (
    definition.kind === 'PURCHASE_REQUEST' || definition.kind === 'SUPPLIER_ONBOARDING' || definition.kind === 'RFQ'
  );
}

/**
 * The kinds drawn wholly from the design16 prototype -- builder and section-wise view as well: PR and Supplier
 * Onboarding. The BID kind keeps its own builder (the sectioned ReviewAndSubmitPage_And_BIDOverview layout) and its
 * own Overview / Sections view, which the new design draws the same way.
 */
export function isDesign16Native(definition: Pick<TemplateDefinition, 'kind'>): boolean {
  return definition.kind === 'PURCHASE_REQUEST' || definition.kind === 'SUPPLIER_ONBOARDING';
}

/** The prototype's Tabler glyph for each PrimeIcon these screens draw. */
const TABLER_OF: Readonly<Record<string, string>> = {
  'pi-eye': 'ti-eye',
  'pi-pencil': 'ti-pencil',
  'pi-copy': 'ti-copy',
  'pi-trash': 'ti-trash',
  'pi-ban': 'ti-circle-off',
  'pi-check-circle': 'ti-circle-check',
  'pi-power-off': 'ti-circle-off',
  'pi-file-excel': 'ti-file-spreadsheet',
  'pi-file': 'ti-file-text',
  'pi-objects-column': 'ti-table',
  'pi-download': 'ti-download',
  'pi-upload': 'ti-upload',
  'pi-file-pdf': 'ti-file-type-pdf',
  'pi-code': 'ti-code',
  'pi-filter': 'ti-filter',
  'pi-replay': 'ti-restore',
  'pi-refresh': 'ti-restore',
  'pi-exclamation-triangle': 'ti-alert-triangle',
  'pi-exclamation-circle': 'ti-alert-circle',
  'pi-info-circle': 'ti-info-circle',
  'pi-check': 'ti-check',
  'pi-times': 'ti-x',
  'pi-file-plus': 'ti-file-plus',
  'pi-save': 'ti-device-floppy',
  'pi-arrow-left': 'ti-arrow-left',
  'pi-plus': 'ti-plus',
  'pi-lock': 'ti-lock',
  'pi-calendar': 'ti-calendar',
  'pi-search': 'ti-search',
  'pi-cog': 'ti-settings-plus',
  'pi-list': 'ti-list-details',
  'pi-clipboard': 'ti-clipboard-list',
  'pi-truck': 'ti-truck-delivery',
  'pi-box': 'ti-package',
  'pi-users': 'ti-users',
  'pi-file-check': 'ti-file-check',
  'pi-shield': 'ti-shield-check',
  'pi-book': 'ti-notes',
};

/** A PrimeIcon name as the class to draw: its Tabler twin on a design16 kind (where one is mapped). */
export function mappedIcon(d16: boolean, prime: string): string {
  const tabler = d16 ? TABLER_OF[prime] : undefined;
  return tabler ? `ti ${tabler}` : `pi ${prime}`;
}

/** The icon class for a control: the prototype's Tabler glyph on a design16 kind, the PrimeIcon elsewhere. */
export function iconClass(d16: boolean, tabler: string, prime: string): string {
  return d16 ? `ti ${tabler}` : `pi ${prime}`;
}
